import React, { useCallback, useMemo, useRef } from "react";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, NumberInput, PanelSection } from "./controls";
import { Button } from "@/components/ui/button";
import { FlipHorizontal2, FlipVertical2, Replace, RotateCw, CornerDownRight } from "lucide-react";

// Figma-style image controls: corner radius (px / %, with optional per-corner
// independent radii), flip, opacity, fit mode, and replace-image. Corner
// radius is applied via a clipPath (path with per-corner arcs) in the renderer.
const FIT_OPTIONS = [
  { value: "none", label: "原始尺寸" },
];

const CORNER_KEYS = ["cornerTL", "cornerTR", "cornerBR", "cornerBL"];
const CORNER_LABELS = { cornerTL: "左上", cornerTR: "右上", cornerBR: "右下", cornerBL: "左下" };

export default function ImageControls({ attrs, setAttr, onReplaceImage }) {
  const fileRef = useRef(null);

  const opacityValue = useMemo(() => [Math.round((attrs.opacity ?? 1) * 100)], [attrs.opacity]);
  const cornerUnit = attrs.cornerRadiusUnit === "%" ? "%" : "px";
  const cornerValue = Number(attrs.cornerRadius) || 0;
  const individual = !!attrs.cornerIndividual;
  // For % mode the meaningful range is 0–50 (0 = square, 50 = full pill).
  const cornerMax = cornerUnit === "%" ? 50 : 200;

  // Switch corner-radius unit. Uses the patch-object form of setAttr so both
  // fields are merged in a single update — two sequential setAttr calls would
  // clobber each other because each rebuilds attrs from the same stale snapshot.
  const setCornerUnit = useCallback(
    (unit) => {
      const max = unit === "%" ? 50 : 200;
      const patch = { cornerRadiusUnit: unit, cornerRadius: Math.min(cornerValue, max) };
      // Also clamp the per-corner values when switching units.
      CORNER_KEYS.forEach((k) => {
        const v = Number(attrs[k]) || 0;
        patch[k] = Math.min(v, max);
      });
      setAttr(patch);
    },
    [setAttr, cornerValue, attrs]
  );

  // Manual numeric input for the (shared) corner radius. Clamps to range.
  const setCornerValue = useCallback(
    (v) => {
      const clamped = Math.max(0, Math.min(v, cornerMax));
      setAttr({ cornerRadius: clamped });
    },
    [setAttr, cornerMax]
  );

  // Toggle independent per-corner mode. When turning it on, seed each corner
  // with the current shared value so the shape doesn't suddenly change.
  const toggleIndividual = useCallback(() => {
    const next = !individual;
    if (next) {
      const patch = { cornerIndividual: true };
      CORNER_KEYS.forEach((k) => { patch[k] = cornerValue; });
      setAttr(patch);
    } else {
      // Turning off: fold the four corners back into the shared value (use TL
      // as the representative value, like Figma does when you collapse).
      setAttr({ cornerIndividual: false, cornerRadius: Number(attrs.cornerTL) || 0 });
    }
  }, [individual, cornerValue, attrs, setAttr]);

  // Set a single corner's radius (independent mode).
  const setSingleCorner = useCallback(
    (key, v) => {
      const clamped = Math.max(0, Math.min(v, cornerMax));
      setAttr({ [key]: clamped });
    },
    [setAttr, cornerMax]
  );

  const flipH = useCallback(() => {
    const cur = Number(attrs.flipH) || 1;
    setAttr("flipH", cur === 1 ? -1 : 1);
  }, [attrs.flipH, setAttr]);

  const flipV = useCallback(() => {
    const cur = Number(attrs.flipV) || 1;
    setAttr("flipV", cur === 1 ? -1 : 1);
  }, [attrs.flipV, setAttr]);

  const handlePickFile = useCallback(() => fileRef.current?.click(), []);

  const handleFileChange = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (file) onReplaceImage?.(file);
      // Reset so picking the same file again still fires change.
      e.target.value = "";
    },
    [onReplaceImage]
  );

  return (
    <PanelSection title="Image">
      {/* Corner radius with px / % unit toggle + manual numeric input (Figma-like).
          An "independent" toggle reveals four per-corner inputs (TL/TR/BR/BL). */}
      <Field label={`Corner Radius · ${individual ? "独立" : `${cornerValue}${cornerUnit}`}`}>
        <div className="flex items-center gap-2">
          <div className="flex-1 pt-1">
            <Slider
              value={[Math.min(cornerValue, cornerMax)]}
              min={0}
              max={cornerMax}
              step={1}
              onValueChange={([v]) => setCornerValue(v)}
              disabled={individual}
              data-testid="image-corner-radius-slider"
            />
          </div>
          <div className="w-16 shrink-0">
            <NumberInput
              value={cornerValue}
              onChange={(v) => setCornerValue(v)}
              disabled={individual}
              testId="image-corner-radius-input"
            />
          </div>
          <div className="flex border border-line bg-chrome shrink-0">
            <button
              type="button"
              onClick={() => setCornerUnit("px")}
              data-testid="image-corner-unit-px"
              className={`px-2 h-8 text-[10px] font-mono uppercase ${cornerUnit === "px" ? "bg-[#002FA7] text-white" : "text-ink3 hover:text-ink"}`}
            >
              px
            </button>
            <button
              type="button"
              onClick={() => setCornerUnit("%")}
              data-testid="image-corner-unit-pct"
              className={`px-2 h-8 text-[10px] font-mono uppercase border-l border-line ${cornerUnit === "%" ? "bg-[#002FA7] text-white" : "text-ink3 hover:text-ink"}`}
            >
              %
            </button>
          </div>
        </div>
        {/* Independent per-corner toggle. */}
        <button
          type="button"
          onClick={toggleIndividual}
          data-testid="image-corner-individual-toggle"
          className={`mt-2 flex items-center gap-1.5 px-2 h-7 text-[10px] font-mono uppercase border border-line ${individual ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-chrome text-ink3 hover:text-ink"}`}
        >
          <CornerDownRight className="h-3 w-3" />
          独立圆角 · Independent
        </button>
        {/* Per-corner inputs (only shown in independent mode). */}
        {individual && (
          <div className="mt-2 grid grid-cols-2 gap-2" data-testid="image-corner-individual-grid">
            {CORNER_KEYS.map((k) => (
              <div key={k} className="flex items-center gap-1.5">
                <span className="text-[10px] font-mono text-ink4 w-8 shrink-0">{CORNER_LABELS[k]}</span>
                <div className="flex-1">
                  <NumberInput
                    value={Number(attrs[k]) || 0}
                    onChange={(v) => setSingleCorner(k, v)}
                    testId={`image-corner-${k}`}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </Field>

      {/* Flip toggles (horizontal / vertical). */}
      <Field label="Flip">
        <div className="grid grid-cols-2 gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={flipH}
            data-testid="image-flip-h-btn"
            className={`h-8 rounded-none border-line bg-chrome text-xs font-mono ${(Number(attrs.flipH) || 1) === -1 ? "bg-[#002FA7] text-white border-[#002FA7]" : "text-ink3 hover:text-ink"}`}
          >
            <FlipHorizontal2 className="h-3.5 w-3.5 mr-1" /> H
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={flipV}
            data-testid="image-flip-v-btn"
            className={`h-8 rounded-none border-line bg-chrome text-xs font-mono ${(Number(attrs.flipV) || 1) === -1 ? "bg-[#002FA7] text-white border-[#002FA7]" : "text-ink3 hover:text-ink"}`}
          >
            <FlipVertical2 className="h-3.5 w-3.5 mr-1" /> V
          </Button>
        </div>
      </Field>

      {/* Fit / preserveAspectRatio mode. */}
      <Field label="Fit">
        <Select value={attrs.preserveAspectRatio || "xMidYMid slice"} onValueChange={(v) => setAttr("preserveAspectRatio", v)}>
          <SelectTrigger data-testid="image-fit-select" className="h-8 rounded-none bg-chrome border-line text-foreground text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-chrome border-line text-foreground rounded-none">
            {FIT_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>

      {/* Opacity. */}
      <Field label={`Opacity · ${opacityValue[0]}%`}>
        <div className="pt-2">
          <Slider
            value={opacityValue}
            min={0}
            max={100}
            step={1}
            onValueChange={([v]) => setAttr("opacity", v / 100)}
            data-testid="image-opacity-slider"
          />
        </div>
      </Field>

      {/* Replace image source. */}
      <Field label="Replace Image">
        <Button
          variant="outline"
          size="sm"
          onClick={handlePickFile}
          data-testid="image-replace-btn"
          className="w-full h-8 rounded-none border-line bg-chrome text-ink3 hover:text-ink text-xs font-mono uppercase"
        >
          <Replace className="h-3.5 w-3.5 mr-1.5" /> Choose file…
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
        />
      </Field>
    </PanelSection>
  );
}
